import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import SiteLookupContext from '../context/SiteLookupContext';
import { SiteLookupDataView, SiteLookupDataViewValue } from '../siteLookupDataView';
import { siteLookupTokens } from '../siteLookupTokens';

const { color, size } = siteLookupTokens;

const LiveDot = () => <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: color.live, flexShrink: 0 }} />;

/**
 * Historical / Real-time source of the left panel. A segmented control with both permissions, otherwise a static
 * label of the only source. Always the same height so the list never shifts.
 */
const DataSourceControl = () => {
  const { t } = useTranslation();
  const { dataView, hasHistoricalAccess, hasRealTimeAccess, maxHistoricalMonths, setDataView } = useContext(SiteLookupContext);

  let content = null;

  if (hasHistoricalAccess && hasRealTimeAccess) {
    content = (
      <ToggleButtonGroup
        exclusive
        aria-label={t('siteLookup.viewToggle.label')}
        value={dataView}
        onChange={(_, nextValue: SiteLookupDataView | null) => nextValue && setDataView(nextValue)}
        sx={{
          height: size.sourceControlHeight,
          padding: '2px',
          borderRadius: '8px',
          backgroundColor: color.segmented.track,
          '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            borderRadius: '6px !important',
            color: color.text.secondary,
            fontSize: 12,
            fontWeight: 600,
            gap: 0.75,
            paddingX: 1.25,
            paddingY: 0,
            textTransform: 'none',
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: color.segmented.active,
              boxShadow: '0 1px 2px rgb(16 24 40 / 12%)',
              color: color.text.primary,
            },
          },
        }}
      >
        <ToggleButton value={SiteLookupDataViewValue.historical}>{t('siteLookup.viewToggle.historical')}</ToggleButton>
        <ToggleButton value={SiteLookupDataViewValue.realtime}>
          <LiveDot />
          {t('siteLookup.viewToggle.realtime')}
        </ToggleButton>
      </ToggleButtonGroup>
    );
  } else if (hasHistoricalAccess) {
    content = (
      <Typography fontSize={12} fontWeight={500} color={color.text.muted}>
        {t('siteLookup.viewToggle.historicalOnly', { count: maxHistoricalMonths })}
      </Typography>
    );
  } else if (hasRealTimeAccess) {
    content = (
      <Typography component="span" display="inline-flex" alignItems="center" gap={0.75} fontSize={12} fontWeight={500} color={color.text.muted}>
        <LiveDot />
        {t('siteLookup.viewToggle.realtime')}
      </Typography>
    );
  }

  return (
    <Box display="flex" alignItems="center" justifyContent="flex-end" flexShrink={0} height={size.sourceControlHeight}>
      {content}
    </Box>
  );
};

export default DataSourceControl;
